import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FilterSection from './FilterSection';
import ProductCard from '../../../components/ProductCard';
import { Product } from '../../../types/types';
import { instance } from '../../../api/axiosInstance'; // API 인스턴스 import
import NoticeComponent from '../../../components/NoticeComponent';

export default function SearchResultsPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [priceRange, setPriceRange] = useState([0, 200000]);
    const [starRating, setStarRating] = useState(1);
    const [isDiscounted, setIsDiscounted] = useState<null | boolean>(null);
    const [selectedHashtags, setSelectedHashtags] = useState<string[]>([]);
    const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const searchTerm = params.get('q') || '';

    useEffect(() => {
        if (!searchTerm) {
            navigate('/'); // If no search term, navigate to home
            return;
        }

        const fetchProducts = async () => {
            setLoading(true);
            try {
                const response = await instance.get(`/api/products/search`, {
                    params: { query: searchTerm }, // API 요청
                });
                setFilteredProducts(response.data);
            } catch (error) {
                console.error('Error fetching products:', error);
                setFilteredProducts([]);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, [searchTerm, navigate]);

    const handleHashtagChange = (hashtags: string[]) => {
        setSelectedHashtags(hashtags);
    };

    const formatNumber = (number: number | bigint) => {
        return new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 3 }).format(number);
    };

    // 추가 필터링 로직
    const finalFilteredProducts = filteredProducts.filter((product) => {
        const isPriceMatch = product.price >= priceRange[0] && product.price <= priceRange[1];
        const isStarMatch = Number(product.star) >= starRating;
        const isDiscountMatch = isDiscounted === null || product.isDiscounted === isDiscounted;
        const isHashtagMatch =
            selectedHashtags.length === 0 ||
            selectedHashtags.every((tag) => product.description.includes(tag));

        return isPriceMatch && isStarMatch && isDiscountMatch && isHashtagMatch;
    });

    return (
        <section className="max-w-[2560px] w-full mx-auto mt-[88px]">
            <div className="mt-[130px] max-w-[1440px] w-full mx-auto relative">
                <div className="text-left">
                    <h2 className="flex items-center text-3xl font-bold text-MainTextColor">
                        '{searchTerm}' 의 상품
                        <span className="font-normal text-[22px] ml-2 text-[#969696]">
                            ({finalFilteredProducts.length})
                        </span>
                    </h2>
                    <p className="font-light text-sm text-[#969696] leading-6">
                        '{searchTerm}' 에 대한 검색 결과입니다.
                    </p>
                </div>

                <article className="pb-[68px] grid grid-cols-5 gap-4">
                    <FilterSection
                        onPriceRangeChange={setPriceRange}
                        onStarRatingChange={setStarRating}
                        onDiscountChange={setIsDiscounted}
                        onHashtagChange={handleHashtagChange}
                        onSearchTermChange={() => {}} // 빈 함수 또는 정의된 함수
                        category="" // No specific category
                    />

                    <div className="col-span-4 grid grid-cols-4 gap-4">
                        {finalFilteredProducts.length > 0 ? (
                            finalFilteredProducts.map((product: Product) => (
                                <ProductCard key={product.id} product={product} formatNumber={formatNumber}  />
                            ))
                        ) : loading ? (
                            <div className="col-span-4 text-center text-gray-500">
                                로딩 중...
                            </div>
                        ) : (
                            <div className="col-span-4 text-center text-gray-500">
                                {t('product.messages.no_products')}
                            </div>
                        )}
                    </div>
                </article>

                <div className="w-full pb-[48px]">
                    <div className="w-full h-[170px] bg-slate-200 rounded-lg"></div>
                </div>
            </div>
        </section>
    );
}
